import { useEffect, useState } from 'react'
import { Column, Data, FieldType, Tooltip } from '../../../ui'
import { useDataTypesHooks } from '../../hooks/dataTypesHooks'
import styles from './dataTypeList.module.css'

// Nombres de registros de otras tablas, por "tabla:id"
export type TableSortLabels = Record<string, string>

const tableLabelKey = (col: Column, id: unknown) => `${col.table}:${id}`

// Las columnas de tipo tabla guardan el id pero muestran el nombre: sin sortValue propio se ordenan por el nombre
const needsTableLabels = (col?: Column): col is Column =>
    !!col && col.type === FieldType.TABLE && !!col.table && !col.sortValue

export const useTableSortLabels = (rows: Data[], col?: Column): TableSortLabels => {
    const { formatTable } = useDataTypesHooks()
    const [labels, setLabels] = useState<TableSortLabels>({})

    const key = col ? (col.sortKey ?? col.name) : undefined
    const missingIds = needsTableLabels(col) ?
        Array.from(new Set(rows.map(row => row[key!]).filter(id => typeof id === 'string' && !(tableLabelKey(col, id) in labels)))) as string[] :
        []
    const missingIdsKey = missingIds.join(',')

    useEffect(() => {
        if (!needsTableLabels(col) || missingIds.length === 0) return
        Promise.all(missingIds.map(async id => {
            try {
                return [tableLabelKey(col, id), await formatTable(col.table!, id)] as const
            } catch {
                return [tableLabelKey(col, id), id] as const
            }
        })).then(entries => setLabels(prev => ({ ...prev, ...Object.fromEntries(entries) })))
    }, [missingIdsKey, col?.name])

    return labels
}

export type SortDirection = 'asc' | 'desc' | null

export type SortState = {
    column: string | null
    direction: SortDirection
}

export const INITIAL_SORT_STATE: SortState = { column: null, direction: null }

export const nextSortState = (prev: SortState, columnName: string): SortState => {
    if (prev.column !== columnName) return { column: columnName, direction: 'asc' }
    if (prev.direction === 'asc') return { column: columnName, direction: 'desc' }
    return { column: null, direction: null }
}

export const sortRows = (rows: Data[], col: Column, direction: 'asc' | 'desc', tableLabels: TableSortLabels = {}): Data[] => {
    const key = col.sortKey ?? col.name
    const useTableLabels = needsTableLabels(col)
    const toSortValue = (raw: Data[string]) => {
        if (col.sortValue) return col.sortValue(raw)
        if (useTableLabels && raw != null) return tableLabels[tableLabelKey(col, raw)] ?? raw
        return raw
    }
    return [...rows].sort((a, b) => {
        const aVal = toSortValue(a[key])
        const bVal = toSortValue(b[key])
        if (aVal == null && bVal == null) return 0
        if (aVal == null) return 1
        if (bVal == null) return -1
        let cmp = 0
        if (!col.sortValue && (col.type === FieldType.NUMBER || col.type === FieldType.PRICE || col.type === FieldType.PERCENTAGE)) {
            cmp = (aVal as number) - (bVal as number)
        } else if (!col.sortValue && col.type === FieldType.DATE) {
            const aTime = aVal instanceof Date ? (aVal as Date).getTime() : new Date(String(aVal)).getTime()
            const bTime = bVal instanceof Date ? (bVal as Date).getTime() : new Date(String(bVal)).getTime()
            cmp = aTime - bTime
        } else if (typeof aVal === 'number' && typeof bVal === 'number') {
            cmp = aVal - bVal
        } else {
            cmp = String(aVal).localeCompare(String(bVal), 'es', { numeric: true, sensitivity: 'base' })
        }
        return direction === 'asc' ? cmp : -cmp
    })
}

// Sin listas acotadas por filtros no se ordena: ordenar carga todos los registros del filtro
export const SORT_BATCH_SIZE = 200
export const MAX_SORT_ITEMS = 1000
export const DEFAULT_SORT_DISABLED_HINT = 'Para ordenar, filtrá por fecha o por cliente'
export const sortLimitExceededMessage = `Hay más de ${MAX_SORT_ITEMS} resultados: acotá los filtros para poder ordenar`

// Con disabledHint los encabezados no ordenan y muestran el motivo al pasar el mouse
export const withSortHeaders = (
    columns: Column[],
    sortState: SortState,
    onColumnSort: (name: string) => void,
    disabledHint?: string
): Column[] =>
    columns.map(col => {
        if (col.sortable === false) return col
        if (disabledHint) {
            return {
                ...col,
                renderLabel: () => (
                    <span className={styles.sortDisabledHeader}>
                        {col.renderLabel ? col.renderLabel() : col.label}
                        <Tooltip tooltip={disabledHint} className={styles.sortTooltip} />
                    </span>
                )
            }
        }
        const isActive = sortState.column === col.name
        const direction = isActive ? sortState.direction : null
        const sortIconClass = `${styles.sortIcon} ${isActive ? styles.sortIconActive : ''}`
        const sortChar = direction === 'asc' ? '↑' : direction === 'desc' ? '↓' : '↕'
        return {
            ...col,
            renderLabel: () => (
                <button
                    type="button"
                    className={styles.sortableHeader}
                    onClick={() => onColumnSort(col.name)}
                >
                    {col.renderLabel ? col.renderLabel() : col.label}
                    <span className={sortIconClass}>
                        {sortChar}
                        {isActive && direction && (
                            <span className={styles.sortLabel}>
                                {direction === 'asc' ? 'a-z' : 'z-a'}
                            </span>
                        )}
                    </span>
                </button>
            )
        }
    })
